import PropTypes from 'prop-types';
import React from 'react';
// -*- mode: jsx;-*-

import { Pagination } from '../common/pagination.js';
import { LoadingEllipsis } from '../common/LoadingEllipsis.js';
import { ProjectCardWithFollowInfo } from './ProjectCardWithFollowInfo.js';
import { Svg } from '../common/Svg.js';
import { TrackSectionView } from '../common/TrackSectionView.js';
import useMobileStatus from '../common/useMobileStatus.js';
import useGiveWidget from '../common/giveWidget/useGiveWidget.js';
import { fetchProjects } from './fetchProjects.js';

/* global Uri */
const getPage = (url) => parseInt(new Uri(url).getParam('page'), 10) || 1;

let FollowContent = function ({
    favoritesLoaded, favorites, followedProjects, baseSearchURL,
    user, totalProjects, setTotalProjects, setFollowedProjects,
}) {
    const [followUrl, setFollowUrl] = React.useState();
    const [loadingProjects, setLoadingProjects] = React.useState(false);
    const [isMobile] = useMobileStatus();
    const [showGiveWidget] = useGiveWidget();
    const topElem = React.useRef(null);

    const loadFollowingProjects = React.useCallback((searchUrl) => {
        if (!loadingProjects) {
            // FavoriteHeart.followCallback updates this cookie to be used as a cache buster
            // That doesn't guarantee it will but updated for *all* favorites changes,
            // but at least some of the most common ones.
            /* global readCookie */
            const followDate = readCookie('followDate');
            if (followDate) {
                searchUrl = new Uri(searchUrl).replaceQueryParam('ver', followDate).toString();
            }

            setLoadingProjects(true);
            setFollowUrl(searchUrl);

            const setProjectData = (data) => {
                setFollowedProjects(data.proposals);
                setTotalProjects(parseInt(data.totalProposals, 10));
                setLoadingProjects(false);

                // Scroll to the top of results if we're loading more,
                // but not if this is the first load of results
                if (followedProjects) {
                    /* global scrollToPageTop */
                    scrollToPageTop($(topElem.current).offset().top);
                }
            };

            const clearProjects = () => {
                setFollowedProjects([]);
                setLoadingProjects(false);
                scrollToPageTop($(topElem.current).offset().top);
            };

            fetchProjects(searchUrl,
                (data) => {
                    // If you have enough follows to have pagination,
                    // then open a separate tab and remove follows so you only have one page of projects,
                    // then return to the homepage without refreshing and try to advance the page,
                    // you'll unexpectedly receive no projects for that page.
                    // We need to reload page one with the new set of projects to address that.
                    if (data.proposals.length === 0 && getPage(searchUrl) > 1) {
                        const startingUrl = new Uri(searchUrl).deleteQueryParam('page').toString();
                        setFollowUrl(startingUrl);
                        fetchProjects(startingUrl, setProjectData, clearProjects);
                        return;
                    }

                    setProjectData(data);
                },
                clearProjects,
            );
        }
    }, [followedProjects, loadingProjects, setFollowedProjects, setTotalProjects]);

    if (favoritesLoaded) {
        if (favorites.schoolIds.length > 0 || favorites.teacherIds.length > 0 || favorites.projectIds.length > 0) {
            if (!followedProjects) {
                loadFollowingProjects(`${baseSearchURL}?donorFollowing=${user.userId}&sortBy=7`);
            } else if (followedProjects.length > 0) {
                const page = getPage(followUrl);
                return <TrackSectionView sectionName="Followed teachers, schools, and projects">
                    <div className="activity-header">
                        <h3>Recent Activity</h3>
                        <p>Teachers, schools, and projects you&apos;re following
                            <span className="tooltip-wrapper">
                                <Svg icon="help"/>
                                <span className="hover-tooltip">By<Svg icon="bookmark"/><b>following</b> any teacher or school, all of their newly posted projects will show up here chronologically by the date the project went live. Any projects you follow will also be included and sorted in this way.</span>
                            </span>
                        </p>
                        <div>
                            <a href={secureBaseURL + 'donors/favorites.html'}>Manage following</a>
                            {' '}<span className="divider">|</span>
                            {' '}<a href={secureBaseURL + 'common/donation-info.html?tab=PROJECTS_SUPPORTED'}>View giving history</a>
                        </div>
                    </div>
                    {loadingProjects ? <LoadingEllipsis className="page-loader"/> : ''}
                    <div ref={topElem}>
                        {followedProjects.map(project =>
                            <ProjectCardWithFollowInfo project={project} key={project.id}
                                showGiveWidget={showGiveWidget}
                                projectIds={favorites.projectIds} schoolIds={favorites.schoolIds}
                                teacherIds={favorites.teacherIds}/>,
                        )}
                    </div>
                    <Pagination max={totalProjects} url={followUrl} page={page} simplePagination={isMobile}
                        isLoading={loadingProjects} changeURL={loadFollowingProjects}/>
                </TrackSectionView>;
            } else {
                if (totalProjects === undefined) {
                    setTotalProjects(0);
                }
                return <div className="follow-prompt no-follow-projects" ref={topElem}>
                    <span>There are no active projects from any of the teachers or schools you follow.</span>
                </div>;
            }
        } else {
            if (totalProjects === undefined) {
                setTotalProjects(0);
            }
            return <div className="follow-prompt no-follows" ref={topElem}>
                        <div className="empty-state-text">
                            <h3>
                                You're not following any teachers or schools
                            </h3>
                            <span>
                                If you <span className="add-favorite example"><span className="favorite-marker"><Svg icon="bookmark"/>Follow</span></span> a teacher or school you care about, we'll gather all of their new requests here!
                            </span>
                        </div>
                    </div>;
        }
    }

    // If no other condition was true, we're still loading results
    return <LoadingEllipsis />;
};
FollowContent.propTypes = {
    baseSearchURL: PropTypes.string.isRequired,
    user: PropTypes.object.isRequired,
    favoritesLoaded: PropTypes.bool.isRequired,
    setTotalProjects: PropTypes.func.isRequired,
    setFollowedProjects: PropTypes.func.isRequired,
    favorites: PropTypes.object,
    followedProjects: PropTypes.array,
    totalProjects: PropTypes.number,
};
FollowContent = React.memo(FollowContent);
export default FollowContent;
